import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Building2,
  Users,
  Check,
  Sparkles,
  Crown,
  Zap,
  Rocket,
  ChevronDown,
  Infinity as InfinityIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";

const YEARLY_DISCOUNT = 35;

const PLANS = [
  {
    id: "starter",
    nameKey: "pricingNew.starter.name",
    monthlyPrice: 59,
    descKey: "pricingNew.starter.ideal",
    featureKeys: [
      "pricingNew.starter.feat1",
      "pricingNew.starter.feat2",
      "pricingNew.starter.feat3",
      "pricingNew.starter.feat4",
      "pricingNew.starter.feat5",
      "pricingNew.starter.feat6",
      "pricingNew.starter.feat7",
      "pricingNew.starter.feat8",
    ],
    hectaresKey: "pricingNew.hectaresStarter",
    users: 25,
    icon: Zap,
  },
  {
    id: "professional",
    nameKey: "pricingNew.pro.name",
    monthlyPrice: 199,
    descKey: "pricingNew.pro.ideal",
    featureKeys: [
      "pricingNew.pro.feat1",
      "pricingNew.pro.feat2",
      "pricingNew.pro.feat3",
      "pricingNew.pro.feat4",
      "pricingNew.pro.feat5",
      "pricingNew.pro.feat6",
      "pricingNew.pro.feat7",
      "pricingNew.pro.feat8",
    ],
    hectaresKey: "pricingNew.hectaresPro",
    users: 75,
    popular: true,
    icon: Rocket,
  },
  {
    id: "enterprise",
    nameKey: "pricingNew.enterprise.name",
    monthlyPrice: 449,
    descKey: "pricingNew.enterprise.ideal",
    featureKeys: [
      "pricingNew.enterprise.feat1",
      "pricingNew.enterprise.feat2",
      "pricingNew.enterprise.feat3",
      "pricingNew.enterprise.feat4",
      "pricingNew.enterprise.feat5",
      "pricingNew.enterprise.feat6",
      "pricingNew.enterprise.feat7",
      "pricingNew.enterprise.feat8",
    ],
    hectaresKey: "pricingNew.hectaresEnterprise",
    users: 999,
    icon: Crown,
  },
];

interface PricingSectionProps {
  trialMode?: string;
}

export function PricingSection({ trialMode }: PricingSectionProps) {
  const trialParam = trialMode ? `&trial=${trialMode}` : "";
  const { t } = useTranslation();
  const [isYearly, setIsYearly] = useState(false);

  // Single shared expansion state across all viewports -> all three expand together
  const [allExpanded, setAllExpanded] = useState(false);

  const getPrice = (monthlyPrice: number) => {
    if (isYearly) {
      return Math.round(monthlyPrice * (1 - YEARLY_DISCOUNT / 100));
    }
    return monthlyPrice;
  };

  const renderPlanCard = (
    plan: typeof PLANS[0],
    index: number,
    variant: "grid" | "carousel",
  ) => {
    const Icon = plan.icon;
    const isEnterprise = plan.id === "enterprise";
    const isPopular = plan.popular;

    const expanded = allExpanded;
    const toggle = () => setAllExpanded((v) => !v);


    const cardContent = (
      <div
        className={cn(
          "p-5 md:p-6 h-full flex flex-col relative",
          isEnterprise &&
            "bg-gradient-to-br from-landing-dark via-gray-900 to-landing-dark rounded-[15px]",
        )}
      >
        {/* Header: icon + name */}
        <div className="flex items-center gap-3 mb-4">
          <div
            className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center",
              isEnterprise
                ? "bg-white/10"
                : "bg-gradient-to-br from-[#3ECDC6]/15 to-[#2B7CDB]/15",
            )}
          >
            <Icon
              className={cn(
                "w-5 h-5",
                isEnterprise ? "text-landing-accent" : "text-[#2B7CDB]",
              )}
            />
          </div>
          <h3
            className={cn(
              "text-2xl font-heading font-extrabold tracking-tight",
              isEnterprise ? "text-white" : "text-landing-text",
            )}
          >
            {t(plan.nameKey)}
          </h3>
        </div>

        {/* Price */}
        <div className="mb-2">
          <div className="flex items-baseline gap-1.5">
            <span
              className={cn(
                "text-5xl font-black tracking-tight",
                isEnterprise ? "text-white" : "text-landing-text",
              )}
            >
              €{getPrice(plan.monthlyPrice)}
            </span>
            <span
              className={cn(
                "text-sm font-medium",
                isEnterprise ? "text-white/70" : "text-landing-text/60",
              )}
            >
              /{t("pricingNew.month")}
            </span>
          </div>
          {isYearly && (
            <p className="text-xs text-[#2B7CDB] font-semibold mt-1">
              {t("pricingNew.savePercent", { percent: YEARLY_DISCOUNT })}
            </p>
          )}
        </div>

        {/* Billing caption */}
        <p
          className={cn(
            "text-sm mb-2",
            isEnterprise ? "text-white/70" : "text-landing-text/60",
          )}
        >
          {t(isYearly ? "pricingNew.billingCaptionYearly" : "pricingNew.billingCaptionMonthly")}
        </p>

        {/* Trial highlight */}
        <div
          className={cn(
            "inline-flex items-center gap-1.5 mb-5 px-3 py-1 rounded-full text-xs font-bold w-fit",
            isEnterprise
              ? "bg-landing-accent/15 text-landing-accent"
              : "bg-green-100 text-green-700",
          )}
        >
          <Sparkles className="w-3 h-3" strokeWidth={2.5} />
          {t("pricingNew.trialBadge")}
        </div>


        {/* Divider */}
        <div
          className={cn(
            "h-px mb-5",
            isEnterprise ? "bg-white/10" : "bg-gray-200",
          )}
        />

        {/* Limits */}
        <div className="space-y-3 mb-6">
          {[
            { Icon: Building2, label: t(plan.hectaresKey), isUnlimited: false },
            {
              Icon: Users,
              label: plan.users === 999
                ? `${t("pricingNew.unlimited")} ${t("pricingNew.users").toLowerCase()}`
                : `${plan.users} ${t("pricingNew.users").toLowerCase()}`,
              isUnlimited: plan.users === 999,
            },
          ].map((row, i) => {
            return (
              <div
                key={i}
                className={cn(
                  "flex items-center gap-3 text-sm",
                  isEnterprise ? "text-white/90" : "text-landing-text",
                )}
              >
                <div
                  className={cn(
                    "w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0",
                    isEnterprise
                      ? "bg-landing-accent/20 text-landing-accent"
                      : "bg-gradient-to-br from-[#3ECDC6] to-[#2B7CDB] text-white",
                  )}
                >
                  {row.isUnlimited ? (
                    <InfinityIcon className="w-3 h-3" strokeWidth={3} />
                  ) : (
                    <Check className="w-3 h-3" strokeWidth={3} />
                  )}
                </div>
                <span className="font-medium">{row.label}</span>
              </div>
            );
          })}
        </div>

        {/* Primary CTA */}
        <div className="mt-auto">
          <Link
            to={`/register?plan=${plan.id}${trialParam}${isYearly ? "&yearly=true" : ""}`}
            className="block w-full"
          >
            <Button
              className={cn(
                "w-full h-12 rounded-full font-bold text-base transition-all duration-300 hover:-translate-y-0.5",
                isPopular
                  ? "bg-gradient-to-r from-[#3ECDC6] to-[#2B7CDB] text-white shadow-lg shadow-[#2B7CDB]/30 hover:opacity-95"
                  : isEnterprise
                    ? "bg-landing-accent text-landing-dark hover:bg-landing-glow"
                    : "bg-white border-2 border-[#2B7CDB] text-[#2B7CDB] hover:bg-[#2B7CDB] hover:text-white",
              )}
            >
              {t("pricingNew.getStarted")}{" "}
              <span className="ml-1 font-bold">
                {t(plan.nameKey)}
              </span>
            </Button>
          </Link>

          {/* Expand button */}
          <button
            type="button"
            onClick={toggle}
            className={cn(
              "w-full mt-2 h-10 flex items-center justify-center gap-1.5 text-sm font-semibold rounded-full transition-colors",
              isEnterprise
                ? "text-white/80 hover:text-white"
                : "text-landing-text/70 hover:text-[#2B7CDB]",
            )}
          >
            {expanded ? t("pricingNew.hideWhatsIncluded") : t("pricingNew.viewWhatsIncluded")}
            <ChevronDown
              className={cn(
                "w-4 h-4 transition-transform duration-300",
                expanded && "rotate-180",
              )}
            />
          </button>
        </div>

        {/* Expanded details */}
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="details"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div
                className={cn(
                  "mt-5 pt-5 border-t",
                  isEnterprise ? "border-white/10" : "border-gray-200",
                )}
              >
                {isEnterprise && (
                  <p
                    className={cn(
                      "text-sm italic mb-4",
                      "text-white/70",
                    )}
                  >
                    {t(plan.descKey)}
                  </p>
                )}

                <ul className="space-y-2.5">
                  {plan.featureKeys.map((featureKey, i) => (
                    <li
                      key={i}
                      className={cn(
                        "flex items-start gap-2.5 text-sm",
                        isEnterprise ? "text-white/85" : "text-landing-text",
                      )}
                    >
                      <Check
                        className={cn(
                          "w-4 h-4 mt-0.5 flex-shrink-0",
                          isEnterprise ? "text-landing-accent" : "text-[#2B7CDB]",
                        )}
                        strokeWidth={3}
                      />
                      <span>{t(featureKey)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );

    // Enterprise card

    if (isEnterprise) {
      return (
        <div
          key={plan.id}
          className="relative rounded-2xl border-2 border-landing-accent shadow-xl shadow-landing-accent/10 hover:shadow-2xl hover:shadow-landing-accent/20 transition-all duration-300"
        >
          {cardContent}
        </div>
      );
    }

    // Popular: gradient border via wrapper
    if (isPopular) {
      return (
        <div
          key={plan.id}
          className="relative rounded-2xl p-[2px] bg-gradient-to-r from-[#3ECDC6] to-[#2B7CDB] shadow-xl shadow-[#2B7CDB]/15 hover:shadow-2xl hover:shadow-[#2B7CDB]/25 transition-all duration-300"
        >
          <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
            <span className="bg-gradient-to-r from-[#3ECDC6] to-[#2B7CDB] text-white text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
              <Sparkles className="w-3 h-3" aria-hidden="true" />
              {t("pricingNew.popular")}
            </span>
          </div>
          <div className="bg-white rounded-[14px] h-full">{cardContent}</div>
        </div>
      );
    }

    // Starter
    return (
      <div
        key={plan.id}
        className="relative rounded-2xl border-2 border-gray-200 bg-white hover:border-[#2B7CDB]/40 hover:shadow-xl transition-all duration-300"
      >
        {cardContent}
      </div>
    );
  };


  return (
    <section
      id="pricing"
      className="relative pt-4 md:pt-14 lg:pt-16 pb-6 md:pb-16 lg:pb-12 bg-gradient-to-b from-white via-gray-50 to-white overflow-hidden scroll-mt-20"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#3ECDC6]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#2B7CDB]/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-4 md:mb-12 lg:mb-10 pt-2 md:pt-8"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block bg-gradient-to-r from-[#3ECDC6]/10 to-[#2B7CDB]/10 text-[#2B7CDB] text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-2 md:mb-4"
          >
            {t("pricingNew.badge") || "Pricing"}
          </motion.span>
          <h2 className="text-[2.15rem] sm:text-3xl md:text-4xl lg:text-4xl font-heading font-extrabold text-landing-text mb-3 md:mb-14 leading-tight">
            <span className="block sm:inline">{t("pricingNew.titlePart1")}</span>{" "}
            <span className="bg-gradient-to-r from-[#3ECDC6] to-[#2B7CDB] text-white px-2 sm:px-3 py-1 rounded-lg inline-block mt-1 sm:mt-0">
              {t("pricingNew.titlePart2")}
            </span>
          </h2>
          <p className="text-xl md:text-lg lg:text-base font-medium md:font-semibold leading-snug text-landing-text max-w-2xl lg:max-w-xl mx-auto mb-2 md:mb-6">
            {t("pricingNew.subtitle")}
          </p>
        </motion.div>

        {/* Billing toggle */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.3 }}
          className="flex flex-col items-center gap-2 md:gap-4 lg:gap-3 mb-4 md:mb-16 lg:mb-12"
        >
          <span className="hidden md:inline-block bg-gradient-to-r from-[#3ECDC6] to-[#2B7CDB] text-white text-sm font-bold px-5 py-2 rounded-full shadow-lg shadow-[#2B7CDB]/25">
            {t("pricingNew.trialBadge")}
          </span>
          <div className="flex items-center justify-center gap-3 md:gap-4 lg:gap-3">
            <span
              className={cn(
                "font-medium transition-colors text-sm",
                !isYearly ? "text-[#2B7CDB]" : "text-landing-text",
              )}
            >
              {t("pricingNew.billingMonthly")}
            </span>
            <Switch
              checked={isYearly}
              onCheckedChange={setIsYearly}
              className="data-[state=checked]:bg-[#2B7CDB]"
            />
            <span
              className={cn(
                "font-medium transition-colors flex items-center gap-2 text-sm",
                isYearly ? "text-[#2B7CDB]" : "text-landing-text",
              )}
            >
              {t("pricingNew.billingYearly")}
              <Badge className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                {t("pricingNew.savePercent", { percent: YEARLY_DISCOUNT })}
              </Badge>
            </span>
          </div>
        </motion.div>

        {/* Mobile carousel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="md:hidden"
        >
          <Carousel opts={{ loop: true, align: "center" }} className="w-full">
            <CarouselContent className="pt-3 pb-1">
              {PLANS.map((plan, index) => (
                <CarouselItem key={plan.id} className="basis-[88%]">
                  {renderPlanCard(plan, index, "carousel")}
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </motion.div>


        {/* Desktop grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="hidden md:grid md:grid-cols-3 gap-5 lg:gap-6 max-w-5xl mx-auto items-stretch"
        >
          {PLANS.map((plan, index) => renderPlanCard(plan, index, "grid"))}
        </motion.div>
      </div>
    </section>
  );
}
